import { useCallback, useState, useEffect } from 'react';

/**
 * Mirrors the backend's OTP lockout on screen (App\Services\Pharmacy\OtpAttemptLimiter).
 *
 * The backend is the one that blocks the attempt; this only counts the wait down so a
 * locked-out user is not left typing codes that cannot be accepted.
 *
 * Nothing is remembered across a reload on purpose. A saved countdown outlived the lockout
 * it came from: support would clear a lockout, the page would still refuse to ask for a
 * code, and the user waited out a block that no longer existed. Reloading now asks the
 * backend, which either sends a code or refuses with the exact wait still to run. Refreshing
 * has never been a way past the lockout, because the backend counts the wrong codes.
 */
const secondsUntil = (lockedUntil) => (
	lockedUntil ? Math.max(Math.ceil((lockedUntil - Date.now()) / 1000), 0) : 0
);

/** "1:05" for anything a minute or longer, "45 seconds" below that. */
export const formatWait = (seconds) => {
	if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;

	const minutes = Math.floor(seconds / 60);
	const remainder = seconds % 60;

	return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

/**
 * @param account the email being signed in. The backend counts wrong codes per login user,
 *                so switching accounts drops a countdown that belongs to someone else.
 */
export const useOtpLockout = (account = '') => {
	const [lockedUntil, setLockedUntil] = useState(null);
	const [secondsLeft, setSecondsLeft] = useState(0);

	// A countdown belongs to the account that earned it.
	useEffect(() => {
		setLockedUntil(null);
	}, [account]);

	useEffect(() => {
		if (!lockedUntil) {
			setSecondsLeft(0);
			return undefined;
		}

		const tick = () => {
			const remaining = secondsUntil(lockedUntil);
			setSecondsLeft(remaining);

			if (remaining <= 0) setLockedUntil(null);
		};

		tick();
		const timer = setInterval(tick, 1000);
		return () => clearInterval(timer);
	}, [lockedUntil]);

	/**
	 * Starts the countdown when the backend reports a lockout (HTTP 429 with otp_locked).
	 * Returns true when this error was a lockout, so the caller can skip its own message.
	 */
	const applyLockoutFromError = useCallback((errorPayload) => {
		const data = errorPayload?.data?.data;

		if (errorPayload?.status !== 429 || !data?.otp_locked) return false;

		const seconds = Number(data.retry_after_seconds);
		const until = Number.isFinite(seconds) && seconds > 0
			? Date.now() + seconds * 1000
			: Date.parse(data.locked_until);

		if (!Number.isFinite(until)) return false;

		setLockedUntil(until);
		return true;
	}, []);

	const clearLockout = useCallback(() => setLockedUntil(null), []);

	return {
		isOtpLocked: secondsLeft > 0,
		otpLockoutSecondsLeft: secondsLeft,
		otpLockoutMessage: secondsLeft > 0
			? `Too many incorrect codes. Please try again in ${formatWait(secondsLeft)}.`
			: '',
		applyLockoutFromError,
		clearLockout,
	};
};

export default useOtpLockout;
